import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { AlertTriangle, ArrowRight, CheckCircle2, XCircle } from 'lucide-react';

const problems = [
  { text: 'Hours spent writing session notes after each patient', emphasis: 'Hours of note-writing' },
  { text: 'Missed clinical cues during sessions due to multitasking', emphasis: 'Missed clinical cues' },
  { text: 'Generic treatment plans that don\'t adapt to patient progress', emphasis: 'Static treatment plans' },
  { text: 'Billing errors and denied claims costing revenue', emphasis: 'Billing errors' },
  { text: 'Language barriers limiting patient access to quality care', emphasis: 'Language barriers' },
  { text: 'Burnout from administrative overload and documentation fatigue', emphasis: 'Provider burnout' },
];

const solutions = [
  { text: 'Comprehensive auto-generated SOAP notes from live sessions', emphasis: 'Auto-generated notes' },
  { text: 'Real-time AI surfaces relevant prompts during therapy', emphasis: 'AI session guidance' },
  { text: 'Treatment plans evolve automatically with every session', emphasis: 'Adaptive care plans' },
  { text: 'One click submission for claims', emphasis: 'Automated billing' },
  { text: 'Multilingual transcription breaks down communication walls', emphasis: 'Multilingual support' },
  { text: 'Spend 70% more time with patients, not paperwork', emphasis: '70% time savings' },
];

export default function ProblemSolution() {
  const { ref, inView } = useScrollAnimation();

  return (
    <section className="section-padding bg-gradient-to-b from-white to-gray-50" ref={ref}>
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4 tracking-tight">
            The Problems That Keep You Up at Night.{' '}
            <span className="gradient-text">Solved.</span>
          </h2>
          <p className="text-lg text-gray-600">
            Every behavioral health provider faces the same challenges.
            Doctorite AI eliminates them at the source.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Problems */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Without Doctorite</h3>
            </div>
            <div className="space-y-3">
              {problems.map((problem, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.3 + index * 0.08 }}
                  className="flex items-start gap-3 p-4 bg-white rounded-xl border border-red-100/50 hover:border-red-200 transition-colors"
                >
                  <XCircle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                  <div>
                    <span className="text-sm text-gray-700">{problem.text}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Solutions */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-accent-100 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-accent-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">With Doctorite AI</h3>
            </div>
            <div className="space-y-3">
              {solutions.map((solution, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 10 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.5 + index * 0.08 }}
                  className="flex items-start gap-3 p-4 bg-white rounded-xl border border-accent-100/50 hover:border-accent-200 transition-colors"
                >
                  <CheckCircle2 className="w-5 h-5 text-accent-500 mt-0.5 shrink-0" />
                  <div>
                    <span className="text-sm text-gray-700">{solution.text}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-12"
        >
          <Link to="/features" className="inline-flex items-center gap-2 text-brand-600 font-semibold hover:text-brand-700 transition-colors group">
            See how it works
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
